import React, { useEffect, useRef, useState } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, IconButton, CircularProgress } from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { Button } from 'react-admin';

/**
 * Shared TextbookViewer component for displaying course material HTML with mermaid diagram support.
 * Used by both ExamWizard and CourseTextbookResources.
 */
const TextbookViewer = ({ open, onClose, title, htmlContent, loading, chapterSrcUrl }) => {
  const contentRef = useRef(null);
  const isFullHtmlDocument = typeof htmlContent === 'string' && /<html[\s>]/i.test(htmlContent);
  const [processedHtml, setProcessedHtml] = useState('');
  const [hasChapters, setHasChapters] = useState(null); // null=unknown, true/false after check

  // Normalize the HTML to a snippet we can safely inject: extract styles and body content
  useEffect(() => {
    if (!open || !htmlContent) {
      setProcessedHtml('');
      setHasChapters(null);
      return;
    }
    if (isFullHtmlDocument) {
      try {
        const parser = new DOMParser();
        const doc = parser.parseFromString(htmlContent, 'text/html');
        // Remove all script tags to avoid non-executing inline scripts cluttering DOM
        doc.querySelectorAll('script').forEach((n) => n.parentNode && n.parentNode.removeChild(n));
        const styles = Array.from(doc.querySelectorAll('style'))
          .map(el => el.innerHTML || '')
          .join('\n');
        const bodyHtml = doc.body ? doc.body.innerHTML : htmlContent;
        const snippet = `${styles ? `<style>${styles}</style>` : ''}${bodyHtml}`;
        setProcessedHtml(snippet);
        // eslint-disable-next-line no-console
        console.log('TextbookViewer: processed full HTML, bytes:', snippet.length);
      } catch (e) {
        // Fallback: if parsing fails, render raw HTML
        setProcessedHtml(htmlContent);
        // eslint-disable-next-line no-console
        console.warn('TextbookViewer: HTML parse failed; using raw content');
      }
    } else {
      setProcessedHtml(htmlContent);
    }
  }, [open, htmlContent, isFullHtmlDocument]);

  // If processed HTML is empty, preflight the student chapters endpoint to avoid noisy 404s
  useEffect(() => {
    if (!open) return;
    // If we already have HTML to show, skip any preflight checks
    if (processedHtml && processedHtml.trim().length > 0) {
      setHasChapters(null);
      return;
    }
    // Only preflight when no HTML content is available at all
    if (!chapterSrcUrl) {
      setHasChapters(false);
      return;
    }
    try {
      const chaptersUrl = chapterSrcUrl.replace(/\/chapter\/\d+$/, '/chapters');
      // Small defer to allow processedHtml to be computed and avoid spurious 404s on first paint
      const timer = setTimeout(() => {
        if (processedHtml && processedHtml.trim().length > 0) return;
        fetch(chaptersUrl, { headers: { 'Accept': 'application/json' }})
          .then(r => (r.ok ? r.json() : Promise.resolve([])))
          .then(arr => {
            setHasChapters(Array.isArray(arr) && arr.length > 0);
          })
          .catch(() => setHasChapters(false));
      }, 120);
      return () => clearTimeout(timer);
    } catch {
      setHasChapters(false);
    }
  }, [open, processedHtml, chapterSrcUrl]);

  useEffect(() => {
    if (open && processedHtml && contentRef.current) {
      // Small delay to ensure DOM is fully rendered
      const timer = setTimeout(async () => {
        if (window.mermaid && contentRef.current) {
          try {
            const mermaidElements = contentRef.current.querySelectorAll('.mermaid');
            // eslint-disable-next-line no-console
            console.log('TextbookViewer: mermaid elements found:', mermaidElements.length);
            if (mermaidElements.length > 0) {
              // Clear any previous mermaid processing
              mermaidElements.forEach(el => {
                if (el.getAttribute('data-processed')) {
                  el.removeAttribute('data-processed');
                }
              });
              // Validate and render each mermaid element individually
              for (const el of mermaidElements) {
                try {
                  // Pre-check the syntax without rendering
                  await window.mermaid.parse(el.textContent || '');
                  // If syntax is valid, render it
                  await window.mermaid.run({ nodes: [el] });
                } catch (err) {
                  // Log the validation error to the backend
                  fetch('/api/log-event', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                      level: 'error',
                      message: 'Mermaid syntax validation failed',
                      extra: {
                        error: err.message,
                        component: 'TextbookViewer',
                        mermaid_code: el.textContent?.substring(0, 500) // Log first 500 chars of bad code
                      }
                    })
                  });
                  // Hide the invalid diagram block
                  el.style.display = 'none';
                }
              }
            }
          } catch (err) {
            // This is a fallback for any unexpected errors during the run
            fetch('/api/log-event', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                level: 'error',
                message: 'An unexpected error occurred during Mermaid processing',
                extra: {
                  error: err.message,
                  component: 'TextbookViewer'
                }
              })
            });
          }
        } else {
          console.warn('TextbookViewer: Mermaid library not loaded');
        }
      }, 150);
      return () => clearTimeout(timer);
    }
  }, [open, processedHtml]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        style: { height: '90vh' }
      }}
    >
      <DialogTitle>
        {title || 'Textbook'}
        <IconButton
          onClick={onClose}
          style={{ position: 'absolute', right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers style={{ height: '100%', display: 'flex' }}>
        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%', padding: '40px' }}>
            <CircularProgress />
          </div>
        ) : processedHtml && processedHtml.trim().length > 0 ? (
          <div
            ref={contentRef}
            dangerouslySetInnerHTML={{ __html: processedHtml }}
            style={{
              fontFamily: 'inherit',
              lineHeight: '1.6',
              maxWidth: 'none',
              width: '100%',
              minHeight: '60vh',
              background: '#fff',
              borderRadius: 4,
              overflow: 'auto',
              padding: 8
            }}
          />
        ) : hasChapters ? (
          <iframe
            title={title || 'Textbook'}
            src={chapterSrcUrl || 'about:blank'}
            style={{ width: '100%', height: '70vh', border: 'none', background: '#fff', borderRadius: 4 }}
          />
        ) : (
          <div style={{ width: '100%', minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#666' }}>
            No textbook content available yet.
          </div>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default TextbookViewer;
